import { TableOfContents } from '@/components/TableOfContents';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { PLANS, formatPlanLimit } from '@logpulse/types/plans';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
        {title}
      </h2>
      <div className="prose-custom">{children}</div>
    </section>
  );
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="mb-8 scroll-mt-20">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
        {title}
      </h3>
      <div className="prose-custom">{children}</div>
    </div>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{children}</p>;
}

function CodeBlock({ children, title }: { children: string; title?: string }) {
  return (
    <div className="my-4 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {title && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400">
          {title}
        </div>
      )}
      <pre className="px-4 py-3 bg-gray-950 text-gray-300 text-sm font-mono overflow-x-auto leading-relaxed">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="my-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-900">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-800">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-b-0 border-gray-100 dark:border-gray-800/50">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-600 dark:text-gray-400 font-mono text-xs">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ type, children }: { type: 'info' | 'tip' | 'warning'; children: React.ReactNode }) {
  const styles = {
    info: 'border-info-500 bg-info-50 dark:bg-info-500/10 text-info-600 dark:text-info-400',
    tip: 'border-success-500 bg-success-50 dark:bg-success-500/10 text-success-600 dark:text-success-400',
    warning: 'border-warning-500 bg-warning-50 dark:bg-warning-500/10 text-warning-600 dark:text-warning-400',
  };
  const labels = { info: 'Note', tip: 'Tip', warning: 'Warning' };
  return (
    <div className={`my-4 p-4 rounded-xl border-l-4 ${styles[type]}`}>
      <div className="font-semibold text-sm mb-1">{labels[type]}</div>
      <div className="text-sm leading-relaxed">{children}</div>
    </div>
  );
}

export function QuickstartPage() {
  useDocumentMeta({ title: 'Quickstart: LogPulse Docs', description: 'Ship your first log to LogPulse in 5 minutes using the HTTP API.' });

  const toc = [
    { id: 'overview', label: 'Overview' },
    { id: 'prerequisites', label: 'Prerequisites' },
    { id: 'step-1-api-key', label: 'Step 1: Get Your API Key' },
    { id: 'step-2-first-log', label: 'Step 2: Send Your First Log' },
    { id: 'step-3-batch', label: 'Step 3: Send a Batch' },
    { id: 'step-4-search', label: 'Step 4: Search Your Logs' },
    { id: 'step-5-alert', label: 'Step 5: Set Up an Alert' },
    { id: 'code-examples', label: 'Code Examples' },
    { id: 'example-python', label: 'Python', level: 1 },
    { id: 'example-nodejs', label: 'Node.js', level: 1 },
    { id: 'next-steps', label: 'Next Steps' },
  ];

  return (
    <div className="flex gap-8">
      <div className="flex-1 min-w-0">
        <Section id="overview" title="Ship Your First Log in 5 Minutes">
          <P>
            This quickstart walks you through everything you need to go from zero to a working
            LogPulse setup. By the end, you will have accomplished three things: ingested log data
            into LogPulse, searched those logs using LPQL, and configured your first alert rule.
          </P>
          <P>
            The entire process takes about five minutes. No agents or SDKs are required for this
            initial setup -- a simple HTTP request is all you need to start sending logs.
          </P>
        </Section>

        <Section id="prerequisites" title="Prerequisites">
          <P>Before you begin, make sure you have the following:</P>
          <Table
            headers={['Requirement', 'Details']}
            rows={[
              ['LogPulse account', `Sign up at app.logpulse.io -- the free tier includes ${formatPlanLimit('free')} ingestion and ${PLANS.free.retentionDays}-day retention.`],
              ['API key', 'You will create one in Step 1. Requires an active LogPulse account.'],
              ['HTTP client', 'curl (pre-installed on macOS and most Linux distributions), or any HTTP client such as Postman, httpie, or wget.'],
            ]}
          />
          <Callout type="tip">
            If you prefer to use a language-specific SDK, skip ahead to the Code Examples section
            for ready-to-use snippets in Python and Node.js.
          </Callout>
        </Section>

        <Section id="step-1-api-key" title="Step 1: Get Your API Key">
          <P>
            Your API key authenticates all requests to the LogPulse ingestion and query APIs.
            Each key can be scoped to specific permissions (ingest-only, read-only, or full access).
          </P>
          <P>To create your first API key:</P>
          <P>
            1. Log in to your LogPulse dashboard at app.logpulse.io.
          </P>
          <P>
            2. Navigate to Integrations, then select HTTP API from the left sidebar.
          </P>
          <P>
            3. Click Create API Key.
          </P>
          <P>
            4. Give the key a descriptive name (for example, "quickstart-test") and select Full Access as the scope.
          </P>
          <P>
            5. Click Create. Copy the key immediately -- it will not be shown again.
          </P>
          <Callout type="warning">
            Store your API key securely. Do not commit it to version control or share it in
            plaintext. For production use, store it in environment variables or a secrets manager.
          </Callout>
          <CodeBlock title="Set your API key as an environment variable">
{`export LOGPULSE_API_KEY="lp_your_api_key_here"`}
          </CodeBlock>
        </Section>

        <Section id="step-2-first-log" title="Step 2: Send Your First Log">
          <P>
            Send a single log entry to LogPulse using the HTTP ingestion API. The endpoint
            accepts JSON payloads with a timestamp, severity level, event, source identifier,
            and optional attributes. All fields are optional with sensible defaults.
          </P>
          <CodeBlock title="cURL -- Send a single log">
{`curl -X POST https://api.logpulse.io/api/v1/logs \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer $LOGPULSE_API_KEY" \\
  -d '{
    "timestamp": "2026-03-21T10:15:30.123Z",
    "level": "info",
    "event": "User login successful",
    "source": "auth-service",
    "attributes": {
      "user_id": "usr_8a3b2c1d",
      "ip_address": "192.168.1.42",
      "method": "oauth2",
      "region": "us-east-1"
    }
  }'`}
          </CodeBlock>
          <P>
            A successful response returns HTTP 200 with a JSON body containing the
            ingestion summary:
          </P>
          <CodeBlock title="Response">
{`{
  "data": {
    "accepted": 1,
    "rejected": 0,
    "timestamp": "2026-03-21T10:15:30.456Z",
    "quotaStatus": {
      "plan": "starter",
      "limitMB": 1024,
      "usedMB": 42.5,
      "percent": 4.15,
      "blocked": false
    }
  }
}`}
          </CodeBlock>
          <Callout type="info">
            Logs are available for search within 2-5 seconds of ingestion. If you do not see
            your log immediately, wait a few seconds and refresh the Log Explorer.
          </Callout>
        </Section>

        <Section id="step-3-batch" title="Step 3: Send a Batch of Logs">
          <P>
            For better throughput, you can send multiple logs in a single request using the same
            endpoint. Instead of a single object, send an array of log entries. The request body
            limit is 10 MB of uncompressed JSON.
          </P>
          <CodeBlock title="cURL -- Send a batch of logs">
{`curl -X POST https://api.logpulse.io/api/v1/logs \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer $LOGPULSE_API_KEY" \\
  -d '[
      {
        "timestamp": "2026-03-21T10:16:00.000Z",
        "level": "error",
        "event": "Database connection timeout after 30s",
        "source": "order-service",
        "attributes": {
          "db_host": "db-primary.internal",
          "timeout_ms": "30000",
          "retry_count": "3"
        }
      },
      {
        "timestamp": "2026-03-21T10:16:01.000Z",
        "level": "warn",
        "event": "Falling back to read replica",
        "source": "order-service",
        "attributes": {
          "db_host": "db-replica-1.internal",
          "fallback_reason": "primary_timeout"
        }
      },
      {
        "timestamp": "2026-03-21T10:16:02.500Z",
        "level": "info",
        "event": "Order processed successfully via replica",
        "source": "order-service",
        "attributes": {
          "order_id": "ord_9f8e7d6c",
          "processing_time_ms": "245"
        }
      }
    ]'`}
          </CodeBlock>
          <P>
            The batch response includes a summary with the count of accepted and rejected entries:
          </P>
          <CodeBlock title="Batch response">
{`{
  "data": {
    "accepted": 3,
    "rejected": 0,
    "timestamp": "2026-03-21T10:16:02.800Z",
    "quotaStatus": {
      "plan": "starter",
      "limitMB": 1024,
      "usedMB": 42.5,
      "percent": 4.15,
      "blocked": false
    }
  }
}`}
          </CodeBlock>
        </Section>

        <Section id="step-4-search" title="Step 4: Search Your Logs">
          <P>
            Now that you have ingested some logs, open the Log Explorer to search and analyze them.
            LogPulse uses LPQL (LogPulse Query Language) for searching, filtering, and aggregating
            log data.
          </P>
          <P>
            1. Navigate to Log Explorer in the left sidebar of your LogPulse dashboard.
          </P>
          <P>
            2. In the search bar, type the following LPQL query to find error-level logs:
          </P>
          <CodeBlock title="LPQL query -- Find recent errors">
{`level=error | head 10`}
          </CodeBlock>
          <P>
            3. Press Enter or click Run Query. You should see the database timeout error from Step 3.
          </P>
          <P>Here are a few more useful queries to try:</P>
          <CodeBlock title="LPQL -- Filter by source and time range">
{`source=order-service level=error | timechart count by level`}
          </CodeBlock>
          <CodeBlock title="LPQL -- Search by attribute value">
{`attributes.order_id="ord_9f8e7d6c"`}
          </CodeBlock>
          <CodeBlock title="LPQL -- Full-text search with aggregation">
{`"connection timeout" | stats count by source | sort -count`}
          </CodeBlock>
          <Callout type="tip">
            Use the time range picker in the top-right corner of Log Explorer to narrow your
            search to a specific window. The default range is the last 15 minutes.
          </Callout>
        </Section>

        <Section id="step-5-alert" title="Step 5: Set Up Your First Alert">
          <P>
            Configure an alert that notifies you when error logs exceed a threshold. This
            ensures you are aware of issues before they impact users.
          </P>
          <P>
            1. Navigate to Anomaly Detection in the left sidebar, then click Create Rule.
          </P>
          <P>
            2. Set the rule name to "High Error Rate".
          </P>
          <P>
            3. Enter the following LPQL condition:
          </P>
          <CodeBlock title="Alert condition">
{`level=error | stats count as error_count | where error_count > 50`}
          </CodeBlock>
          <P>
            4. Set the evaluation window to 5 minutes and the evaluation interval to 1 minute.
          </P>
          <P>
            5. Under Notification Channel, select your preferred channel (email, Slack, or PagerDuty).
            If you have not configured a channel yet, click Add Channel and follow the setup wizard.
          </P>
          <P>
            6. Set the severity to Warning and click Save Rule.
          </P>
          <Callout type="info">
            The alert will trigger when more than 50 error-level logs are detected within any
            5-minute window. You can adjust the threshold and window to match your application's
            normal error rate.
          </Callout>
        </Section>

        <Section id="code-examples" title="Code Examples">
          <SubSection id="example-python" title="Python">
            <CodeBlock title="Python -- Send logs with requests">
{`import requests
import json
from datetime import datetime, timezone

LOGPULSE_API_KEY = "lp_your_api_key_here"
LOGPULSE_URL = "https://api.logpulse.io/api/v1/logs"

def send_log(level, event, source, attributes=None):
    payload = {
        "timestamp": datetime.now(timezone.utc).isoformat(),
        "level": level,
        "event": event,
        "source": source,
        "attributes": attributes or {}
    }

    response = requests.post(
        LOGPULSE_URL,
        headers={
            "Content-Type": "application/json",
            "Authorization": f"Bearer {LOGPULSE_API_KEY}"
        },
        json=payload
    )
    response.raise_for_status()
    return response.json()

# Send an info log
result = send_log(
    level="info",
    event="Payment processed successfully",
    source="billing-service",
    attributes={
        "amount_cents": "4999",
        "currency": "USD",
        "customer_id": "cus_abc123"
    }
)
print(f"Accepted: {result['data']['accepted']}")`}
            </CodeBlock>
          </SubSection>

          <SubSection id="example-nodejs" title="Node.js">
            <CodeBlock title="Node.js -- Send logs with fetch">
{`const LOGPULSE_API_KEY = "lp_your_api_key_here";
const LOGPULSE_URL = "https://api.logpulse.io/api/v1/logs";

async function sendLog(level, event, source, attributes = {}) {
  const payload = {
    timestamp: new Date().toISOString(),
    level,
    event,
    source,
    attributes,
  };

  const response = await fetch(LOGPULSE_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": \`Bearer \${LOGPULSE_API_KEY}\`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(\`Ingestion failed: \${response.status}\`);
  }

  return response.json();
}

// Send an error log
const result = await sendLog(
  "error",
  "Failed to connect to cache layer",
  "api-gateway",
  {
    cache_host: "redis-primary.internal",
    error_code: "ECONNREFUSED",
    retry_attempt: "1",
  }
);
console.log("Accepted:", result.data.accepted);`}
            </CodeBlock>
          </SubSection>
        </Section>

        <Section id="next-steps" title="Next Steps">
          <P>
            You now have a working LogPulse setup with log ingestion, search, and alerting.
            Here are some recommended next steps to expand your configuration:
          </P>
          <Table
            headers={['Topic', 'Description']}
            rows={[
              ['LPQL Syntax Reference', 'Learn the full query language for advanced filtering, aggregations, and transformations.'],
              ['HTTP API Documentation', 'Complete API reference for ingestion, querying, and management endpoints.'],
              ['Vector Agent Setup', 'Install the Vector agent for automatic log collection from files, syslog, and other sources.'],
              ['Kubernetes Integration', 'Deploy LogPulse as a DaemonSet to collect container logs from your Kubernetes cluster.'],
              ['Pipelines', 'Build data transformation pipelines to parse, enrich, and route logs before storage.'],
              ['Dashboard & UI Guide', 'Create custom dashboards with widgets, charts, and saved searches.'],
              ['Alerting & Notifications', 'Configure advanced alert rules with escalation policies and multiple notification channels.'],
            ]}
          />
          <Callout type="tip">
            For production deployments, we recommend using the Vector agent instead of direct
            HTTP ingestion. The agent handles batching, retries, backpressure, and local buffering
            automatically.
          </Callout>

          <SubSection id="ai-logging-guide" title="Building with AI? Ship logs from day one.">
            <P>
              If you use Cursor, Copilot, Windsurf, or Claude to write your code, give your AI assistant
              instant knowledge of the LogPulse data model, structured attributes, OTLP setup, and
              production-ready logger patterns, so every line of logging code it generates is correct
              from the start.
            </P>
            <P>
              Point your AI at a single URL and it will understand endpoints, schemas, best practices,
              and even LPQL queries:
            </P>
            <CodeBlock title="Give this to your AI assistant">{`https://api.logpulse.io/api/v1/ai/logging-guide?language=nodejs`}</CodeBlock>
            <P>
              <a
                href="/docs/integrations/ai-logging-guide"
                className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
              >
                Read the full AI Logging Guide setup instructions &rarr;
              </a>
            </P>
          </SubSection>
        </Section>
      </div>

      <TableOfContents items={toc} />
    </div>
  );
}
